import React, { useState, useEffect } from 'react';
import { 
  Button, 
  makeStyles, 
  TextField, 
  Typography, 
  Grid, 
  Select, 
  MenuItem, 
  InputLabel
} from '@material-ui/core';
import Dialog from '../ui/Dialog';
import DatePicker from '../DatePicker/DatePicker.component';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay.component';
import { getAgencyList ,updateFundingApplication} from '../../services/funding.service';

const UpdateApplication = ({ data }) => {
  const [openModal, setOpenModal] = useState(false);
  const [openConfirmation, setConfirmation] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [errorInputMessage, setErrorInputMessage] = useState(null);
  const [touchedFields, setTouchedFields] = useState({
    agency_name: false,
    status: false
  });
  const [agencyList, setAgencyList] = useState([]);
  
  const [applicationDetails, setApplicationDetails] = useState({
    agency_name: '',
    status: '',
    submission_date: null,
    approval_date: null,
    rejection_date: null,
    valid_until: null,
    coordinator_notes: ''
  });

  const useStyles = makeStyles(() => ({
    field: {
      marginBottom: 10,
      width: '100%'
    },
    select: {
      width: '100%'
    },
    formControl: {
      width: '100%'
    },
    errorText: {
      color: 'red',
      fontSize: '0.75rem',
      marginTop: 3,
    }
  }));

  const classes = useStyles();

  useEffect(() => {
    const fetchAgencyList = async () => {
      try {
        const { result } = await getAgencyList({
          program_id: data.program_id          
        });
        
        let agencies = [];
        
        if (result) {
          agencies = result;
        }
        
        agencies.unshift({ id: '', funding_agency_name: 'Select Agency' });
        setAgencyList(agencies);
      } catch (error) {
        console.error('Error fetching agency list:', error);
      }
    };

    fetchAgencyList();
  }, []);

  useEffect(() => {
    if (data) {  
      console.log('data-updatescreen',data);
      setApplicationDetails({
        agency_name: data.funding_agency_id || '',
        status: data.status || '',
        submission_date: data.submission_date || null,
        approval_date: data.approval_date || null,
        rejection_date: data.rejection_date || null,
        valid_until: data.valid_until || null,
        coordinator_notes: data.notes || ''
      });
    }
  }, [data]);

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    if (!applicationDetails.agency_name) {
      errors.agency_name = 'Agency Name is required';
      isValid = false;
    }

    if (!applicationDetails.status) {
      errors.status = 'Status is required';
      isValid = false;
    }
    if (applicationDetails.status === 'submitted' && !applicationDetails.submission_date) {
      errors.submission_date = 'Submission date is required';
      isValid = false;
    }

    if (applicationDetails.status === 'approved') {
      if (!applicationDetails.approval_date) {
        errors.approval_date = 'Approval date is required';
        isValid = false;
      }
      if (!applicationDetails.valid_until) {
        errors.valid_until = 'Valid until date is required';
        isValid = false;
      }
    }

    if (applicationDetails.status === 'rejected' && !applicationDetails.rejection_date) {
      errors.rejection_date = 'Rejection date is required';
      isValid = false;
    }


    setErrorInputMessage(errors);
    return isValid;
  };

  const handleFieldBlur = (fieldName) => {
    setTouchedFields(prev => ({
      ...prev,
      [fieldName]: true
    }));
    
    if (!applicationDetails[fieldName]) {
      setErrorInputMessage(prev => ({
        ...prev,
        [fieldName]: `${fieldName.replace('_', ' ')} is required`
      }));
    }
  };

  const onClick = () => {
    setOpenModal(true);
    setApplicationDetails({
      agency_name: data.funding_agency_id || '',
      status: data.status || '',
      submission_date: data.submission_date || null,
      approval_date: data.approval_date || null,
      rejection_date: data.rejection_date || null,
      valid_until: data.valid_until || null,
      coordinator_notes: data.notes || ''
    });
  };

  const onSubmit = async () => {
    if (!validateForm()) {
      setTouchedFields({
        agency_name: true,
        status: true
      });
      return;
    }

    try {
      setLoading(true); 

const {
  agency_name,
  coordinator_notes,
  ...restOfDetails  
} = applicationDetails;

const payload = {
  ...restOfDetails,
  user_id: data.patient_id,
  funding_agency_id: agency_name,
  notes: coordinator_notes
};


const { result } = await updateFundingApplication({
  program_id: data.program_id,
  id:         data.application_id,
  payload
});

      if (result) {
        setOpenModal(false);
        setConfirmation(true);
      }
    } catch(err) {
      if (err && err.inputs) {
        setErrorInputMessage(err.inputs);
      } else if (err && err.message) {
        setErrorMessage(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const onFieldInput = (e) => {
    const { name, value } = e.target;
    
    if (name === 'status') {
      setApplicationDetails(prevState => ({
        ...prevState,
        [name]: value,
        approval_date: value === 'Approved' ? new Date() : null,
        rejection_date: value === 'Rejected' ? new Date() : null,
        valid_until: value === 'Approved' ? new Date(new Date().setMonth(new Date().getMonth() + 6)) : null
      }));
    } else {
      setApplicationDetails(prevState => ({
        ...prevState,
        [name]: value
      }));
    }

    if (value && errorInputMessage?.[name]) {
      setErrorInputMessage(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const onDateChange = (date, fieldName) => {
    setApplicationDetails(prevState => ({
      ...prevState,
      [fieldName]: date
    }));
  };

  const handleClose = (reload) => {
    setOpenModal(false);
    setConfirmation(false);
    setErrorMessage(null);
    setErrorInputMessage(null);
    setTouchedFields({
      agency_name: false,
      status: false
    });

    if (reload) {
      window.location.reload();
    }
  };

  const formContent = (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <InputLabel>Agency Name *</InputLabel>
          <Select
            required
            label='Agency Name'
            name="agency_name"
            value={applicationDetails.agency_name}
            variant='outlined'
            className={classes.field}
            onChange={onFieldInput}
            onBlur={() => handleFieldBlur('agency_name')}
            error={!!(touchedFields.agency_name && errorInputMessage?.agency_name)}
          >
            {agencyList.map(agency => (
              <MenuItem key={agency.id} value={agency.id}>
                {agency.funding_agency_name}
              </MenuItem>
            ))}
          </Select>
          {touchedFields.agency_name && errorInputMessage?.agency_name && (
            <Typography className={classes.errorText}>
              {errorInputMessage.agency_name}
            </Typography>
          )}
        </Grid>
        
        <Grid item xs={12}>
          <InputLabel>Status *</InputLabel>
          <Select
            required
            label='Status'
            name="status"
            value={applicationDetails.status}
            variant='outlined'
            className={classes.field}
            onChange={onFieldInput}
            onBlur={() => handleFieldBlur('status')}
            error={!!(touchedFields.status && errorInputMessage?.status)}
          >
            <MenuItem value="">Select Status</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
            <MenuItem value="submitted">Submitted</MenuItem>
          </Select>
          {touchedFields.status && errorInputMessage?.status && (
            <Typography className={classes.errorText}>
              {errorInputMessage.status}
            </Typography>
          )}
        </Grid>
        {applicationDetails.status === 'submitted' && (
                <Grid item xs={12}>
                  <DatePicker
                    label='Submission Date *'
                    value={applicationDetails.submission_date}
                    onChange={(date) => onDateChange(date, 'submission_date')}
                    className={classes.field}
                  />
                  {errorInputMessage?.submission_date && (
                    <Typography className={classes.errorText}>
                      {errorInputMessage.submission_date}
                    </Typography>
                  )}
                </Grid>
              )}
              
              {applicationDetails.status === 'approved' && (
                <>
                  <Grid item xs={12}>
                    <DatePicker
                      label='Approval Date *'
                      value={applicationDetails.approval_date}
                      onChange={(date) => onDateChange(date, 'approval_date')}
                      className={classes.field}
                    />
                    {errorInputMessage?.approval_date && (
                      <Typography className={classes.errorText}>
                        {errorInputMessage.approval_date}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <DatePicker
                      label='Valid Until *'
                      value={applicationDetails.valid_until}
                      onChange={(date) => onDateChange(date, 'valid_until')}
                      className={classes.field}
                    />
                    {errorInputMessage?.valid_until && (
                      <Typography className={classes.errorText}>
                        {errorInputMessage.valid_until}
                      </Typography>
                    )}
                  </Grid>
                </>
              )}
              
              {applicationDetails.status === 'rejected' && (
                <Grid item xs={12}>
                  <DatePicker
                    label='Rejection Date *'
                    value={applicationDetails.rejection_date}
                    onChange={(date) => onDateChange(date, 'rejection_date')}
                    className={classes.field}
                  />
                  {errorInputMessage?.rejection_date && (
                    <Typography className={classes.errorText}>
                      {errorInputMessage.rejection_date}
                    </Typography>
                  )}
                </Grid>
              )}
        
        <Grid item xs={12}>
          <TextField
            label="Program Coordinator's Notes"
            name="coordinator_notes"
            value={applicationDetails.coordinator_notes}
            variant='outlined'
            className={classes.field}
            onChange={onFieldInput}
            multiline
            rows={4}
            fullWidth
          />
        </Grid>
      </Grid>
      {errorMessage && (
        <Typography className={classes.errorText}>
          {errorMessage}
        </Typography>
      )}
    </>
  );

  const formButtons = (
    <>
      <Button onClick={() => handleClose(false)} variant='contained'>
        Close
      </Button>
      <Button 
        onClick={onSubmit} 
        variant='contained' 
        color='secondary'
        disabled={isLoading}
      >
        {isLoading ? 'Updating...' : 'Update'}
      </Button>
    </>
  );

  if (isLoading) {
    return <LoadingOverlay />;
  }

  return (
    <>
      <Dialog
        open={openModal}
        title='Update Application'
        content={formContent}
        children={formButtons}
        onSubmit={onSubmit}
        onClose={() => handleClose(false)}
      />
      <Dialog 
        key='confirmation'
        open={openConfirmation}
        title='Confirmation'
        content='Application successfully updated!' 
        children={(
          <Button onClick={() => handleClose(true)} variant='contained'>
            Close
          </Button>
        )} 
        onSubmit={onSubmit}
        onClose={() => handleClose(true)} 
      />
      <Button
        variant="contained"
        color="primary"
        onClick={onClick}
        style={{ marginRight: 8, marginBottom: 10 }}
      >
        Edit
      </Button>
    </>
  );
};

export default UpdateApplication;